import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Email from "@mui/icons-material/Email";
import Phone from "@mui/icons-material/Phone";
import Place from "@mui/icons-material/Place";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarConfig } from "../../../domain/calendarConfig";
import { Loading } from "../../components/shared/Loading";
import { usePublicConfigs } from "../../hooks/index";
import { useI18n } from "../../hooks/useI18n";
import { useQueries } from "../../hooks/useQueries";
import { useServiceBus } from "../../hooks/useServiceBus";
import { theme } from "../../layout/Theme";

export const BookAppointment = ({ calendarUrl }: { calendarUrl: string }) => {
  const { t } = useI18n();
  const queries = useQueries();
  const publicConfigs = usePublicConfigs();
  const serviceBus = useServiceBus();
  const [centerConfigId, setCenterConfigId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const authenticateIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe === null) return;

    serviceBus.dispatch({ type: "iframeAuthentication", iframe });
  }, [iframeRef, serviceBus]);

  useEffect(() => {
    queries.getPublicConfigs.call();
  }, [queries]);

  useEffect(() => {
    if (centerConfigId) {
      authenticateIframe();
    }
  }, [centerConfigId, authenticateIframe]);

  const handleBackToSelection = () => {
    setCenterConfigId(null);
  };

  const filteredConfigs = publicConfigs?.filter((config) => {
    const term = searchTerm.toLowerCase();
    return (
      config.name.toLowerCase().includes(term) ||
      config.address.toLowerCase().includes(term) ||
      config.phoneNumber?.toLowerCase().includes(term) ||
      config.email?.toLowerCase().includes(term)
    );
  });

  return centerConfigId ? (
    <div style={{ height: "100%", position: "relative" }}>
      <Button
        size="large"
        startIcon={<ArrowBackIcon sx={{ width: 28, height: 28 }} />}
        sx={{ position: "absolute", left: 16, top: 2, p: 0 }}
        onClick={handleBackToSelection}
      >
        <Typography variant="h6">{t("common.cta.back")}</Typography>
      </Button>
      <iframe
        ref={iframeRef}
        id="healthrecord-selection-iframe"
        src={`${calendarUrl}/heathrecord_selection?configId=${centerConfigId}&primaryColor=${theme.palette.primary[500]?.replace("#", "%23")}`}
        width="100%"
        height="100%"
        style={{ border: 0, backgroundColor: "white", borderRadius: "8px", padding: "10px" }}
      />
    </div>
  ) : publicConfigs === undefined ? (
    <Loading />
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        placeholder={t("appointments.searchPlaceholder")}
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        slotProps={{
          input: {
            sx: { backgroundColor: "white" },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 2 }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
            xxl: "repeat(3, 1fr)",
          },
          gap: 2,
        }}
      >
        {filteredConfigs?.map((config) => (
          <PublicConfigCard key={config.id} config={config} handleOnClick={setCenterConfigId} />
        ))}
      </Box>
    </Box>
  );
};

const PublicConfigCard = ({
  config,
  handleOnClick,
}: {
  config: CalendarConfig;
  handleOnClick: (id: string) => void;
}) => {
  return (
    <Box
      key={config.id}
      onClick={() => handleOnClick(config.id)}
      sx={{
        p: 2,
        gap: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        border: "1px solid",
        borderColor: theme.palette.grey[400],
        borderRadius: 2,
        cursor: "pointer",
        "&:hover": {
          borderColor: theme.palette.grey[700],
        },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
        {config.name}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Place fontSize="small" color="action" />
        <Typography variant="body2">{config.address}</Typography>
      </Box>
      {config.phoneNumber ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Phone fontSize="small" color="action" />
          <Typography variant="body2">Téléphone: {config.phoneNumber}</Typography>
        </Box>
      ) : null}
      {config.email ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Email fontSize="small" color="action" />
          <Typography variant="body2">Email: {config.email}</Typography>
        </Box>
      ) : null}
    </Box>
  );
};
